import { instance } from "../../lib/api";
import Constants from "../../utils/CommonConstants";
import {
  REFUND_DOCUMENT_TYPES,
  REFUND_CONSTANTS,
  APPROVAL_STATUS,
} from "../../utils/refundConstants";
import {
  formatAmount,
  formatEntryData,
  formatDate,
  extractRefundOpportunityData,
  convertMediaIdsToAttachments,
} from "../../utils/refundHelper";

export const REFUND_ACTION_TYPES = {
  FETCH_PAGE_DATA: "FETCH_PAGE_DATA",
  PAGE_LOADING: "PAGE_LOADING",
  PAGE_LOADED: "PAGE_LOADED",
  REFUND_OPPORTUNITY_DATA: "REFUND_OPPORTUNITY_DATA",
  GET_REFUND_TABULAR_DATA: "GET_REFUND_TABULAR_DATA",
  CLEAR_TABULAR_DATA: "CLEAR_TABULAR_DATA",
  SET_REFUND_STATUS: "SET_REFUND_STATUS",
  SET_TABULAR_PAGE_LOADING: "SET_TABULAR_PAGE_LOADING",
  CLEAR_REFUND_DATA: "CLEAR_REFUND_DATA",
};

const dispatchError = (err, dispatch) => {
  dispatch({
    type: Constants.API_FAIL,
    errMsg: Constants.getErrorMessage(err),
  });
};

export const fetchRefundPendingCount = async (agentId) => {
  const response = await fetchRefundRequestData({ agentId, countOnly: true });
  return response.data;
};

export const fetchRefundRequestData = async (options, dispatch = () => {}) => {
  const {
    agentId,
    status = "PendingApproval",
    lastId = null,
    countOnly = false,
  } = options;
  dispatch(setRefundTabularPageLoading(true));

  const url = "/sapna/v1/approval/get-pending-approvals";

  try {
    const params = { agentId, status };

    if (countOnly) {
      params.countOnly = true;
    }

    if (lastId) {
      params.lastId = lastId;
    }
    const response = await instance.get(url, { params });

    if (!countOnly && response.data) {
      dispatch({
        type: REFUND_ACTION_TYPES.GET_REFUND_TABULAR_DATA,
        payload: {
          currentStatus: status,
          data: response.data.data || {},
        },
      });

      dispatch({
        type: Constants.API_SUCCESS,
        data: response.data,
        errMsg: "Refund Request Details Fetched",
      });
    }
    return response.data;
  } catch (error) {
    dispatchError(error, dispatch);
  } finally {
    dispatch(setRefundTabularPageLoading(false));
  }
};

// Fetch opportunity package refund details
export const getOpportunityPackageRefundDetails =
  (opportunityId, showToast = false) =>
  async (dispatch) => {
    const url = `/sapna/v1/get-opportunity-package-refund-details?opportunityId=${opportunityId}`;
    dispatch({
      type: REFUND_ACTION_TYPES.PAGE_LOADING,
    });
    try {
      const response = await instance({
        url,
        method: "GET",
        withCredentials: true,
      });
      const opportunityData = response.data || {};
      const data = opportunityData.data || {};

      if (data && data.entries && Array.isArray(data.entries)) {
        data.entries = data.entries.map(formatEntryData);
      }

      const packagesData = opportunityData.data;
      const refundOpportunityData = extractRefundOpportunityData(packagesData) || {};

      dispatch({
        type: REFUND_ACTION_TYPES.REFUND_OPPORTUNITY_DATA,
        payload: refundOpportunityData,
      });
      if (showToast) {
        dispatch({
          type: Constants.API_SUCCESS,
          data: opportunityData,
          errMsg: "Package details fetched",
        });
      }
    } catch (err) {
      if (showToast) {
        const errMsg = Constants.getErrorMessage(err);

        dispatch({
          type: Constants.API_FAIL,
          errMsg: errMsg,
        });
      }
    } finally {
      dispatch({
        type: REFUND_ACTION_TYPES.PAGE_LOADED,
      });
    }
  };

// Upload single file to Pixie API for refund attachments
export const uploadRefundFileToPixie =
  (file, opportunityId, documentType = REFUND_DOCUMENT_TYPES.COMMON) =>
  (dispatch) => {
    const formData = new FormData();
    formData.append("objectId", opportunityId);
    formData.append("objectType", "SalesOpportunity");
    formData.append("documentType", "RefundDocument");
    formData.append("domainId", 5);
    formData.append("file", file);

    const url = "pixie/data/v1/entity/document";

    return instance({
      url,
      method: "POST",
      data: formData,
    })
      .then(async (response) => {
        // The response now contains media IDs that need to be converted to openable URLs
        const mediaId = response.data.id;

        // Get the openable URL for the uploaded file
        const openableUrlResponse = await getOpenableUrlsFromMediaIds([mediaId])(
          dispatch
        );

        if (openableUrlResponse && openableUrlResponse.length > 0) {
          const fileData = openableUrlResponse[0];
          return {
            id: fileData.id,
            absolute_url: fileData.absoluteUrl,
            url: fileData.url,
            sizeInBytes: fileData.sizeInBytes,
            fileName: file.name,
            section:
              documentType === REFUND_DOCUMENT_TYPES.COMMON
                ? "common"
                : documentType === REFUND_DOCUMENT_TYPES.FINANCE_HEAD_APPROVAL
                ? "finance"
                : "product",
          };
        } else {
          throw new Error("Failed to get openable URL for uploaded file");
        }
      })
      .catch((err) => {
        const errMsg = Constants.getErrorMessage(err);
        dispatch({
          type: Constants.API_FAIL,
          errMsg: errMsg,
        });
        throw err;
      });
  };

// Get openable URLs from media IDs
export const getOpenableUrlsFromMediaIds = (mediaIdList) => (dispatch) => {
  const mediaIdsString = Array.isArray(mediaIdList)
    ? mediaIdList.join(",")
    : mediaIdList;

  const url = `pixie/data/v2/entity/document?mediaIdList=${mediaIdsString}`;

  return instance({
    url,
    method: "GET",
  })
    .then((response) => {
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return [];
    })
    .catch((err) => {
      const errMsg = Constants.getErrorMessage(err);
      dispatch({
        type: Constants.API_FAIL,
        errMsg: errMsg,
      });
      throw err;
    });
};

// Get refund details with attachment URLs
export const getRefundDetails = (refundId) => async (dispatch) => {
  const url = `/sapna/v1/get-owner-refund-details?refundId=${refundId}`;
  dispatch({
    type: REFUND_ACTION_TYPES.PAGE_LOADING,
  });
  try {
    const response = await instance({
      url,
      method: "GET",
      withCredentials: true,
    });

    const refundData = response.data.data;

    // Format root level amounts
    refundData.refundedAmount = formatAmount(refundData.refundedAmount);
    refundData.approvedAmount = formatAmount(refundData.approvedAmount);
    refundData.lastCancellationDate = formatDate(refundData.lastCancellationDate);
    refundData.refundCreatedAt = formatDate(refundData.refundCreatedAt);

    // Process entries array
    if (refundData.entries && refundData.entries.length > 0) {
      refundData.entries = refundData.entries.map(formatEntryData);

      for (const entry of refundData.entries) {
        if (entry.attachmentUrls && entry.attachmentUrls.length > 0) {
          entry.attachments = await convertMediaIdsToAttachments(
            entry.attachmentUrls,
            dispatch,
            getOpenableUrlsFromMediaIds,
            { useFileName: true }
          );
        } else {
          entry.attachments = [];
        }
      }
    }

    const refundOpportunityData = extractRefundOpportunityData(refundData) || {};

    dispatch({
      type: REFUND_ACTION_TYPES.REFUND_OPPORTUNITY_DATA,
      payload: refundOpportunityData,
    });

    dispatch({
      type: Constants.API_SUCCESS,
      data: refundData,
      errMsg: "Refund details fetched",
    });
  } catch (err) {
    const errMsg = Constants.getErrorMessage(err);
    dispatch({
      type: Constants.API_FAIL,
      errMsg: errMsg,
    });
  } finally {
    dispatch({
      type: REFUND_ACTION_TYPES.PAGE_LOADED,
    });
  }
};

// Send refund approval request
export const sendRefundApproval = (approvalData) => (dispatch) => {
  const {
    opportunityId,
    opportunityPackageMappingV2,
    remark,
    attachmentUrls = [],
  } = approvalData;

  const payload = {
    opportunityId,
    opportunityPackageMappingV2,
    remark,
    attachmentUrls,
  };

  const url = "/sapna/owner-refund";

  return instance({
    url,
    method: "POST",
    data: payload,
    withCredentials: true,
  })
    .then(({ data }) => {
      dispatch({
        type: Constants.API_SUCCESS,
        data: data,
        duration: 5000,
        errMsg: "Refund Request Raised Successfully",
      });
      return data;
    })
    .catch((err) => {
      const errMsg = Constants.getErrorMessage(err);
      dispatch({
        type: Constants.API_FAIL,
        errMsg: errMsg,
      });
    });
};

// Approve or reject refund request
export const approveRejectRefund = (refundData) => (dispatch) => {
  const { refundId, managerRemark, status } = refundData;
  const payload = {
    taskEntityId: refundId,
    remark: managerRemark,
    approvalType: status,
  };

  const url = "/sapna/v4/task";

  return instance({
    url,
    method: "POST",
    data: payload,
    withCredentials: true,
  })
    .then(({ data }) => {
      const successMessage =
        status === APPROVAL_STATUS.APPROVED
          ? REFUND_CONSTANTS.REFUND_WAS_APPROVED
          : REFUND_CONSTANTS.REFUND_WAS_REJECTED;

      dispatch({
        type: Constants.API_SUCCESS,
        data: data,
        duration: 5000,
        errMsg: successMessage,
      });
      return data;
    })
    .catch((err) => {
      const errMsg = Constants.getErrorMessage(err);
      dispatch({
        type: Constants.API_FAIL,
        errMsg,
      });
    });
};

export const clearRefundTabularData = () => ({
  type: REFUND_ACTION_TYPES.CLEAR_TABULAR_DATA,
});

export const clearRefundData = () => ({
  type: REFUND_ACTION_TYPES.CLEAR_REFUND_DATA,
});

export const setRefundCurrentStatus = (status) => ({
  type: REFUND_ACTION_TYPES.SET_REFUND_STATUS,
  payload: {
    currentStatus: status || "PendingApproval",
  },
});

export const setRefundTabularPageLoading = (isDataLoading) => ({
  type: REFUND_ACTION_TYPES.SET_TABULAR_PAGE_LOADING,
  payload: isDataLoading,
});
